import { useEffect, useState } from "react";
import classnames from "classnames";
import TableMobileRow from "./TableMobileRow";
import TableCellOverlay from "./TableCellOverlay";
import { ColumnDef } from "types/common/Header";

interface Props<T extends { id: number }> {
  data: T[];
  headers: ColumnDef<T>[];
  entityType: string;
  onDelete: (entityId: string) => void;
}

const BaseTableMobile = <T extends { id: number }>({
  data,
  headers,
  entityType,
  onDelete,
}: Props<T>) => {
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setActiveRowId(null);
    setShowModal(false);
  }, [data]);

  const processEntity = (entity: T) => {
    const { id } = entity;
    const entityIdStr = id.toString();
    const nameHeader = headers.find((h) => h.sortKey === "name");
    const title = nameHeader ? nameHeader.cell(entity) : entityIdStr;
    const mobileHeaders = headers.filter((h) => h.sortKey !== "name");
    return { id, entityIdStr, title, mobileHeaders };
  };

  const toggleAccordion = (id: number) => {
    setOpenAccordions((prev) =>
      prev.includes(id) ? prev.filter((acc) => acc !== id) : [...prev, id]
    );
  };

  const handleRowClick = (entityId: string) => {
    if (activeRowId === entityId) {
      setActiveRowId(null);
      setShowModal(false);
    } else {
      setActiveRowId(entityId);
      setShowModal(true);
    }
  };

  const handleCloseOverlay = () => {
    setActiveRowId(null);
    setShowModal(false);
  };

  const renderAccordionRow = (entity: T) => {
    const { id, entityIdStr, title, mobileHeaders } = processEntity(entity);
    const isActive = activeRowId === entityIdStr;
    const isOpenAccordion = openAccordions.includes(id);

    const mobileRows = mobileHeaders.map((header) => (
      <TableMobileRow
        key={header.sortKey}
        translationKey={`usAdmin.${header.sortKey}`}
        value={header.cell(entity)}
      />
    ));

    return (
      <div
        key={entityIdStr}
        className="accordion accordion--mobile-table"
        onClick={() => handleRowClick(entityIdStr)}
      >
        <button
          className={classnames(
            "accordion__trigger",
            "table-mobile-accordion__toggler",
            "usadmin__table-mobile__button-entity",
            { "accordion__trigger--is-open": isOpenAccordion }
          )}
          onClick={(e) => {
            e.stopPropagation();
            toggleAccordion(id);
          }}
        >
          <div className="table-mobile-accordion__toggle-title">{title}</div>
        </button>
        <div
          className={classnames("accordion__content", {
            "accordion__content--is-open usadmin--mobile-table__layer":
              isOpenAccordion,
          })}
        >
          {isActive && (
            <TableCellOverlay
              entityId={entityIdStr}
              showModal={showModal}
              onClose={handleCloseOverlay}
              entityType={entityType}
              onDelete={onDelete}
            />
          )}
          {mobileRows}
        </div>
      </div>
    );
  };

  const accordionRows = data.map(renderAccordionRow);

  return <div className="table-mobile">{accordionRows}</div>;
};

export default BaseTableMobile;
